import { Box, CircularProgress, Grid, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import React from 'react';

const useStyles = makeStyles(() => ({
  title: {
    fontWeight: 600,
    color: 'grey',
    marginTop: 10,
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: '8px',
    textTransform: 'capitalize'
  },
}));

function progressReducer(state: number, action: { payload: number; type: 'update' }) {
  const value = action.payload;
  if (Number.isNaN(value)) return 0;
  switch (action.type) {
    case 'update':
      return state === Math.round(value) ? state : Math.round((state + Math.round(value)) / 2);
    default:
      return state;
  }
}

export default function PercentageFitCircle({
  value,
  title,
  size = 40,
  thickness = 3.6,
}: {
  value: number;
  title: 'fit' | 'reliability';
  size?: number;
  thickness?: number;
}) {
  const classes = useStyles();
  const [progress, dispatchProgress] = React.useReducer(progressReducer, 0);

  React.useEffect(() => {
    let iterations = 0;
    if (iterations > 10) {
      dispatchProgress({ type: 'update', payload: value });
    } // Make progress equal to value (without timer) if we've had more than 10 iterations.
    const timer = setInterval(() => {
      if (Number.isNaN(value)) {
        clearInterval(timer);
        return;
      } // If the value is NaN stop the timer
      dispatchProgress({ type: 'update', payload: value });
      iterations += 1;
      if (iterations > 10) {
        clearInterval(timer);
      }
    }, 100);
    // clean up
    return () => {
      clearInterval(timer);
    };
  }, [value]);

  const getColor = React.useCallback((percentProgress) => {
    if (100 - percentProgress === 0) return 'darkgreen';
    if (100 - Math.abs(percentProgress) <= 1) return 'green';
    if (100 - Math.abs(percentProgress) <= 2) return 'darkorange';
    if (100 - Math.abs(percentProgress) > 2) return 'darkred';
    if (typeof percentProgress !== 'number') return 'grey';
    return 'grey';
  }, []);

  return (
    <Grid item className={classes.container}>
      <Box position='relative' display='inline-flex'>
        <CircularProgress
          variant='determinate'
          value={value > 0 ? progress : 100}
          size={size}
          thickness={thickness}
          color='inherit'
          style={{ color: value > 0 ? getColor(progress) : 'lightgrey' }}
        />
        <Box
          top={0}
          left={0}
          bottom={0}
          right={0}
          position='absolute'
          display='flex'
          alignItems='center'
          justifyContent='center'
        >
          <Typography
            variant='caption'
            component='div'
            color='textSecondary'
            align='center'
           
          >
            {progress > 0 ? `${progress}%` : 'No measures exist'}
          </Typography>
        </Box>
      </Box>
      <Typography align='center' variant='subtitle1' className={classes.title}>
        {title}
      </Typography>
    </Grid>
  );
}

PercentageFitCircle.defaultProps = {
  size: 80,
  thickness: 4,
};
